// The Pod writes that have to be undone when a later one fails.
//
// Three paths write more than one thing: Confirm (mapping, then aggregations,
// then the cubes), minting an aggregate, and un-minting one. Each hand-rolled
// its own try/catch, and each got it wrong in the same direction — one
// discarded the result of its own rollback, two let a failing rollback throw
// away the error that caused it. Both mistakes report a Pod that was put back
// when it was not, which is the one thing a rollback must never claim falsely.
//
// The `io` bag is passed in rather than imported. Rollback is behaviour that
// only appears when a write fails, and injection is the only way to make one
// fail on cue — a live Pod does not break to order, and module mocking would
// mean running the suite behind an experimental flag.

// The names a failure can carry back. Declared here rather than written out at
// each site because the Confirm screen groups them into phases: a name that
// drifts on one side silently stops matching on the other, and the screen would
// blame the wrong phase with no error to show for it.
export const STEPS = {
  check: 'check the design',
  mapping: 'write mapping.json',
  aggregations: 'write aggregations.json',
  recompute: 'recompute the private cubes',
  deleteCube: 'delete the removed cube',
};

// A step is { name, run, undo }. `undo` is optional: a step that leaves nothing
// worth restoring simply has none.
export async function transact(steps) {
  const done = [];
  const results = [];
  for (const step of steps) {
    try {
      results.push(await step.run());
    } catch (error) {
      error.step = step.name;
      await unwind(done, error);
      throw error;
    }
    done.push(step);
  }
  return results;
}

// Undo the completed steps in reverse, and never let a failure here hide the
// failure that caused it. An operator told only "the recompute failed" will
// assume the config was restored — and the case where it was not is exactly
// the case they need to hear about.
async function unwind(done, error) {
  const failures = [];
  for (const step of [...done].reverse()) {
    if (!step.undo) continue;
    try {
      await step.undo();
    } catch (e) {
      failures.push(`${step.name}: ${e.message}`);
    }
  }
  if (!failures.length) return;
  error.rollback = failures;
  error.message = `${error.message} — and the Pod could not be put back (${failures.join('; ')})`;
}

// No undo, deliberately. A cube is derived: the next recompute rebuilds it in
// full from the records and the restored config, and nothing on these paths
// publishes, so a half-written cube is stale rather than wrong in kind while
// the config that governs it is restored. Snapshotting them would buy
// file-level atomicity for data that is already reproducible.
const recomputeStep = ({ io, connector, agg, recordContainers }) => ({
  name: STEPS.recompute,
  run: () => io.recompute({ connector, agg, recordContainers }),
});

const writeAggregationsStep = ({ io, aggregations, previous }) => ({
  name: STEPS.aggregations,
  run: () => io.writeAggregations(aggregations),
  undo: () => io.writeAggregations(previous.aggregations),
});

export const confirmSteps = ({ io, sourceId, connector, mapping, aggregations, previous, recordContainers }) => [
  {
    name: STEPS.mapping,
    run: () => io.writeMappingFor(sourceId, mapping),
    undo: () => io.writeMappingFor(sourceId, previous.mapping),
  },
  writeAggregationsStep({ io, aggregations, previous }),
  recomputeStep({ io, connector, agg: aggregations, recordContainers }),
];

export const saveAggregateSteps = ({ io, connector, aggregations, previous, recordContainers }) => [
  writeAggregationsStep({ io, aggregations, previous }),
  recomputeStep({ io, connector, agg: aggregations, recordContainers }),
];

// Recompute before deleting, not after. The delete is the only irreversible
// step here, so it goes last: if the recompute fails, the rollback restores an
// aggregations.json whose cube is still on the Pod, matching it. The other
// order left the config listing an aggregate whose cube file was already gone.
export const deleteAggregateSteps = ({ io, connector, aggregations, previous, recordContainers, cubeUrl }) => [
  writeAggregationsStep({ io, aggregations, previous }),
  recomputeStep({ io, connector, agg: aggregations, recordContainers }),
  {
    // The file has to go: a full recompute rewrites the aggregates it knows
    // about and would leave this one behind — still readable, still
    // publishable, after the analyst removed it.
    name: STEPS.deleteCube,
    run: () => io.deleteResource(cubeUrl),
  },
];
